/**
 * Room Service Menu Data
 * Phase D2.4: Deep Room Service voice demo
 */

export interface MenuItem {
  id: string;
  name: string;
  price: number;
  category: 'all-day' | 'breakfast' | 'drinks';
  aliases: string[]; // Voice recognition aliases
}

export interface MenuCategory {
  id: string;
  name: string;
  items: MenuItem[];
}

export const menuItems: MenuItem[] = [
  // All Day
  {
    id: 'cheeseburger',
    name: 'Cheeseburger',
    price: 18,
    category: 'all-day',
    aliases: ['cheeseburger', 'cheese burger', 'burger', 'hamburger'],
  },
  {
    id: 'caesar-salad',
    name: 'Caesar Salad',
    price: 14,
    category: 'all-day',
    aliases: ['caesar salad', 'caesar', 'salad'],
  },
  {
    id: 'margherita-pizza',
    name: 'Margherita Pizza',
    price: 16,
    category: 'all-day',
    aliases: ['margherita pizza', 'margherita', 'pizza'],
  },
  // Breakfast
  {
    id: 'continental-breakfast',
    name: 'Continental Breakfast',
    price: 22,
    category: 'breakfast',
    aliases: ['continental breakfast', 'continental', 'breakfast'],
  },
  {
    id: 'avocado-toast',
    name: 'Avocado Toast',
    price: 15,
    category: 'breakfast',
    aliases: ['avocado toast', 'avocado', 'toast'],
  },
  // Drinks
  {
    id: 'coca-cola',
    name: 'Coca-Cola',
    price: 5,
    category: 'drinks',
    aliases: ['coca-cola', 'coca cola', 'coke', 'cola'],
  },
  {
    id: 'orange-juice',
    name: 'Fresh Orange Juice',
    price: 6,
    category: 'drinks',
    aliases: ['orange juice', 'fresh orange juice', 'oj', 'juice'],
  },
  {
    id: 'sparkling-water',
    name: 'Sparkling Water',
    price: 4,
    category: 'drinks',
    aliases: ['sparkling water', 'sparkling', 'water', 'mineral water'],
  },
];

export const menuCategories: MenuCategory[] = [
  {
    id: 'breakfast',
    name: 'Breakfast',
    items: menuItems.filter((item) => item.category === 'breakfast'),
  },
  {
    id: 'all-day',
    name: 'All Day',
    items: menuItems.filter((item) => item.category === 'all-day'),
  },
  {
    id: 'drinks',
    name: 'Drinks',
    items: menuItems.filter((item) => item.category === 'drinks'),
  },
];

// Combined menu items in order: Breakfast, All Day, Drinks
export const allMenuItems: MenuItem[] = [
  ...menuItems.filter((item) => item.category === 'breakfast'),
  ...menuItems.filter((item) => item.category === 'all-day'),
  ...menuItems.filter((item) => item.category === 'drinks'),
];

/**
 * Find a menu item by voice input (case-insensitive alias matching)
 */
export function findMenuItemByVoice(input: string): MenuItem | null {
  const normalizedInput = input.toLowerCase().trim();

  // First try exact alias match
  for (const item of menuItems) {
    if (item.aliases.some((alias) => normalizedInput.includes(alias))) {
      return item;
    }
  }

  return null;
}

/**
 * Parse quantity from voice input (e.g., "two burgers" -> 2)
 */
export function parseQuantityFromVoice(input: string): number {
  const normalizedInput = input.toLowerCase().trim();

  // Word-to-number mapping
  const wordNumbers: Record<string, number> = {
    one: 1,
    a: 1,
    an: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
  };

  // Check for word numbers at the start
  for (const [word, num] of Object.entries(wordNumbers)) {
    if (normalizedInput.startsWith(word + ' ')) {
      return num;
    }
  }

  // Check for digit numbers
  const digitMatch = normalizedInput.match(/^(\d+)\s/);
  if (digitMatch && digitMatch[1]) {
    return parseInt(digitMatch[1], 10);
  }

  // Default to 1
  return 1;
}

/**
 * Order item for tracking during conversation
 */
export interface OrderItem {
  menuItem: MenuItem;
  quantity: number;
}

/**
 * Calculate order total
 */
export function calculateOrderTotal(items: OrderItem[]): number {
  return items.reduce((total, item) => total + item.menuItem.price * item.quantity, 0);
}

/**
 * Format order for speech
 */
export function formatOrderForSpeech(items: OrderItem[]): string {
  if (items.length === 0) return 'nothing';

  const itemStrings = items.map((item) => {
    if (item.quantity === 1) {
      return item.menuItem.name;
    }
    return `${item.quantity} ${item.menuItem.name}${item.quantity > 1 ? 's' : ''}`;
  });

  if (itemStrings.length === 1) return itemStrings[0] ?? '';
  if (itemStrings.length === 2) return `${itemStrings[0]} and ${itemStrings[1]}`;

  const last = itemStrings.pop();
  return `${itemStrings.join(', ')}, and ${last ?? ''}`;
}
